import { useParams } from 'react-router-dom';
import { useContact, useCommunications } from '@/hooks/useCRM';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Phone, Mail, MessageSquare, FileText, Plus } from 'lucide-react';
import { format } from 'date-fns';
import LeadScoreBadge from '@/components/crm/LeadScoreBadge';
import BantBreakdown from '@/components/crm/BantBreakdown';

const CHANNEL_ICONS: Record<string, typeof Phone> = {
  phone: Phone,
  email: Mail,
  sms: MessageSquare,
  note: FileText,
};

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: contact, isLoading } = useContact(id!);
  const { data: comms = [] } = useCommunications(id!);

  if (isLoading || !contact) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={`${contact.first_name} ${contact.last_name}`}
        subtitle={`${contact.title ?? ''} — ${contact.customer_name}`}
      >
        <LeadScoreBadge score={contact.lead_score} size="lg" />
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Contact Info */}
        <Card>
          <CardHeader><CardTitle>Contact Info</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" />{contact.email}</p>
            {contact.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" />{contact.phone}</p>}
            <div className="flex flex-wrap gap-1 pt-2">
              {contact.tags.map(t => <Badge key={t} variant="outline">{t}</Badge>)}
              {contact.is_primary && <Badge>Primary</Badge>}
            </div>
            {contact.notes && <p className="pt-2 text-muted-foreground">{contact.notes}</p>}
          </CardContent>
        </Card>

        {/* BANT Score */}
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>BANT Score</CardTitle></CardHeader>
          <CardContent>
            <BantBreakdown
              budget={{ confirmed: contact.budget_confirmed, range: contact.budget_range }}
              authority={{ isDecisionMaker: contact.is_decision_maker, level: contact.authority_level }}
              need={{ identified: contact.need_identified, urgency: contact.need_urgency }}
              timeline={contact.timeline}
              totalScore={contact.lead_score}
            />
          </CardContent>
        </Card>
      </div>

      {/* Communication Timeline */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Communication Timeline</CardTitle>
          <Button size="sm" className="gap-2"><Plus className="h-4 w-4" /> Log Activity</Button>
        </CardHeader>
        <CardContent>
          {comms.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No communications logged yet</p>
          ) : (
            <div className="relative space-y-4 pl-6 before:absolute before:left-2 before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-border">
              {comms.map(comm => {
                const Icon = CHANNEL_ICONS[comm.channel] || FileText;
                return (
                  <div key={comm.id} className="relative">
                    <div className="absolute -left-6 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-background ring-2 ring-border">
                      <Icon className="h-2.5 w-2.5 text-muted-foreground" />
                    </div>
                    <div className="rounded-md border border-border p-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{comm.channel}</Badge>
                        <Badge variant="outline" className="text-[10px]">{comm.direction}</Badge>
                        {comm.subject && <span className="text-sm font-medium">{comm.subject}</span>}
                      </div>
                      <p className="mt-1 text-sm text-foreground">{comm.body}</p>
                      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{comm.logged_by_name}</span>
                        <span>{format(new Date(comm.occurred_at), 'MMM d, yyyy h:mm a')}</span>
                        {comm.duration_seconds && <span>{Math.round(comm.duration_seconds / 60)} min</span>}
                        {comm.outcome && <Badge variant="outline" className="text-[10px]">{comm.outcome}</Badge>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
